import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, ExternalLink, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDowntime = (sec) => {
  if (!sec) return '0s';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
};

const formatRelTime = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const StatusBadge = ({ down }) => (
  <span
    data-testid={down ? 'monitor-status-down' : 'monitor-status-up'}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
      down ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
    }`}
  >
    {down ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
    {down ? 'DOWN' : 'UP'}
  </span>
);

export const SystemHealthPanel = () => {
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const [sRes, iRes] = await Promise.all([
        fetch(`${API}/api/uptime/summary?days=${d}`, { headers: authHeaders() }),
        fetch(`${API}/api/uptime/incidents?limit=20`, { headers: authHeaders() }),
      ]);
      if (sRes.ok) setSummary(await sRes.json());
      else setSummary(null);
      if (iRes.ok) {
        const j = await iRes.json();
        setIncidents(j.incidents || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  if (loading && !summary) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 mt-6" data-testid="system-health-loading">
        <div className="text-sm text-slate-500">Loading system health…</div>
      </div>
    );
  }

  const monitors = summary?.monitors || [];
  const configured = summary?.configured;
  const overall = summary?.overall_uptime_pct ?? 100;
  const anyDown = summary?.any_down;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mt-6" data-testid="system-health-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className={`w-5 h-5 ${anyDown ? 'text-red-600' : 'text-emerald-600'}`} />
          <h3 className="text-lg font-semibold text-slate-900">System Health</h3>
          {anyDown && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold animate-pulse">
              Incident in progress
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="system-health-days"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            data-testid="system-health-refresh"
            onClick={() => load(days)}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!configured && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          UptimeRobot webhook not configured. Set <code className="px-1 bg-amber-100 rounded">UPTIME_WEBHOOK_SECRET</code> in
          backend <code className="px-1 bg-amber-100 rounded">.env</code> and point UptimeRobot to{' '}
          <code className="px-1 bg-amber-100 rounded">{API}/api/uptime/webhook?token=&lt;secret&gt;</code>.
        </div>
      )}

      {monitors.length === 0 && configured && (
        <div className="px-4 py-6 text-center text-sm text-slate-500" data-testid="system-health-empty">
          No incidents recorded yet. Once UptimeRobot fires its first alert, monitors will appear here.
        </div>
      )}

      {monitors.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Overall uptime</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="overall-uptime-pct">
                {overall.toFixed(3)}%
              </div>
              <div className="text-xs text-slate-500 mt-0.5">last {days} days</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Monitors</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{monitors.length}</div>
              <div className="text-xs text-slate-500 mt-0.5">{monitors.filter(m => !m.currently_down).length} up</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Incidents</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {monitors.reduce((s, m) => s + (m.incidents || 0), 0)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">resolved in window</div>
            </div>
          </div>

          <div className="space-y-2 mb-5">
            {monitors.map((m) => (
              <div
                key={m.monitor_id}
                data-testid={`monitor-row-${m.monitor_id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge down={m.currently_down} />
                    <span className="font-medium text-slate-900 truncate">{m.monitor_name}</span>
                  </div>
                  {m.monitor_url && (
                    <a
                      href={m.monitor_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-0.5"
                    >
                      <ExternalLink className="w-3 h-3" /> {m.monitor_url}
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-semibold ${m.uptime_pct >= 99 ? 'text-emerald-700' : m.uptime_pct >= 95 ? 'text-amber-700' : 'text-red-700'}`}>
                    {m.uptime_pct.toFixed(3)}%
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDowntime(m.downtime_seconds)} down · {m.incidents} incidents
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {incidents.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Recent events
          </h4>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="incidents-timeline">
            {incidents.map((inc) => (
              <div
                key={inc.id}
                className="flex items-center gap-3 px-3 py-2 text-sm border-b border-slate-100 last:border-0"
              >
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    inc.alert_type === 1 ? 'bg-red-500' : inc.alert_type === 2 ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                />
                <span className="font-medium text-slate-700 truncate flex-1">{inc.monitor_name}</span>
                <span className="text-xs text-slate-500 shrink-0">{inc.alert_type_name || '—'}</span>
                {inc.alert_type === 2 && inc.duration_seconds > 0 && (
                  <span className="text-xs text-slate-500 shrink-0">({formatDowntime(inc.duration_seconds)})</span>
                )}
                <span className="text-xs text-slate-400 shrink-0 w-20 text-right">{formatRelTime(inc.occurred_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealthPanel;
